import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  LayoutDashboard,
  FileWarning,
  AlertTriangle,
  Code,
  History,
  Settings,
  X,
} from "lucide-react";
import { hasCapability, type Capability } from "@/lib/capabilities";
import { cn, Button } from "@headerhawk/ui";
import { ConnectedUsageMeter, ScriptHashNavEntry } from "@/components/usage";
import { useSubscription } from "@/lib/hooks/use-subscription";
import { useUsage } from "@/lib/hooks/use-usage";

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  capability?: Capability;
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const navigation: NavItem[] = [
  { name: "Overview", href: "/dashboard", icon: LayoutDashboard },
  { name: "Issues", href: "/dashboard/issues", icon: AlertTriangle },
  { name: "Reports", href: "/dashboard/reports", icon: FileWarning },
  {
    name: "Scripts",
    href: "/dashboard/scripts",
    icon: Code,
    capability: "scripts.inventory",
  },
  { name: "Reliability", href: "/dashboard/reliability", icon: Activity },
  {
    name: "Browser changes",
    href: "/dashboard/browser-changes",
    icon: History,
  },
  { name: "Settings", href: "/dashboard/settings", icon: Settings },
];

export function Sidebar({ open, onClose }: SidebarProps) {
  const location = useLocation();
  const { data: subscription } = useSubscription();
  const plan = subscription?.plan ?? "free";
  const { data: usage } = useUsage();
  const scriptHashReportCount = usage?.scriptHashReportCount ?? 0;

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 shrink-0 flex-col border-r bg-background transition-transform duration-300 ease-in-out lg:static lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Mobile close button */}
        <div className="flex h-16 items-center justify-between border-b px-4 lg:hidden">
          <span className="text-lg font-semibold">HeaderHawk</span>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
            <span className="sr-only">Close sidebar</span>
          </Button>
        </div>

        {/* Logo for desktop */}
        <div className="hidden h-16 items-center border-b px-6 lg:flex">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="text-lg font-semibold">HeaderHawk</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navigation.map((item) => {
            if (item.capability && !hasCapability(plan, item.capability)) {
              // A plan without the Scripts view still pays quota for hash
              // reports, so it sees their count where the entry would be
              // (task 264). Only once the plan is known: until then `plan`
              // reads "free", and a Team customer would be shown an upsell.
              return item.capability === "scripts.inventory" &&
                subscription &&
                scriptHashReportCount > 0 ? (
                <ScriptHashNavEntry
                  key={item.name}
                  count={scriptHashReportCount}
                  onNavigate={onClose}
                />
              ) : null;
            }
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Usage Meter */}
        <div className="border-t px-4 py-4">
          <ConnectedUsageMeter compact />
        </div>
      </aside>
    </>
  );
}
